import React, { useState, useEffect, useContext } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Card, Alert } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import Message from '../components/Message.js'
import Loader from '../components/Loader.js'
import { receiptUpdate, getReceipt } from '../actions/receiptActions.js'
import { RECEIPT_DETAIL_RESET } from '../constants/receiptConstants.js'
import { ThemeContext } from '../components/ThemeContext.js'
import { API_URL } from '../apiUrl.js'

const ReceiptUpdateScreen = () => {
  const { theme } = useContext(ThemeContext)
  const { id } = useParams()

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [rcategory, setRcategory] = useState('')
  const [rmeta, setRmeta] = useState('')
  const [rcptext, setRcptext] = useState('')
  const [imageError, setImageError] = useState(false)

  const [alert, setAlert] = useState({
    show: false,
    message: '',
    variant: 'success'
  })

  const showAlert = (message, variant = 'success', seconds = 1500) => {
    setAlert({
      show: true,
      message,
      variant
    })

    setTimeout(() => {
      setAlert({
        show: false,
        message: '',
        variant: 'success'
      })
    }, seconds)
  }

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const detailReceipt = useSelector((state) => state.detailReceipt)
  const { loading, error, receipt } = detailReceipt

  const updateReceipt = useSelector((state) => state.updateReceipt)
  const { success } = updateReceipt

  useEffect(() => {
    if (!userInfo) {
      navigate('/login')
    } else {
      if ((!receipt || receipt._id !== id) && (success || !loading)) {
        dispatch({ type: RECEIPT_DETAIL_RESET })
        dispatch(getReceipt(id))
      } else {
        setRcategory(receipt.rcategory)
        setRmeta(receipt.rmeta)
        setRcptext(receipt.rcptext)
      }
    }
  }, [receipt, dispatch, id, navigate, success, loading, userInfo])

  useEffect(() => {
    if (success) {
      showAlert('Receipt Updated', 'success')
      dispatch({ type: RECEIPT_DETAIL_RESET })
      dispatch(getReceipt(id))
    }
  }, [success, id, dispatch])

  useEffect(() => {
    setImageError(false)
  }, [receipt?.qfname])

  const submitHandler = (e) => {
    e.preventDefault()
    window.confirm('Are you sure?')
    dispatch(receiptUpdate({ _id: id, rcategory, rmeta, rcptext }))
    showAlert('Receipt Updated', 'success')
  }

  const formatDate = (value) => {
    if (!value) return ''
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
      .format(new Date(value))
      .replace(/,/g, '')
  }

  const getReceiptImageSrc = (qfname) => {
    return `${API_URL}/receipts/image/${encodeURIComponent(qfname)}`
  }

  return (
    <Container>
      {alert.show && <Alert variant={alert.variant}>{alert.message}</Alert>}
      {loading && <Loader />}
      {error && <Message variant='danger'>{error}</Message>}

      <Row className='my-2'>
        <Col>
          <Button
            as={Link}
            to='/receipts'
            variant='outline-primary'
            style={{
              borderColor: theme.accent,
              color: theme.accent,
              backgroundColor: 'transparent'
            }}
          >
            Go Back
          </Button>
        </Col>
      </Row>

      <h4>Edit Receipt {formatDate(receipt.createdAt)}</h4>

      <Card
        className='mt-2 mb-2'
        style={{
          backgroundColor: theme.card,
          color: theme.text,
          borderColor: theme.sidebarAccent
        }}
      >
        <Card.Body>
          <Form onSubmit={submitHandler}>
            <Row className='my-2'>
              <Col>
                <img
                  src={getReceiptImageSrc(receipt.qfname)}
                  alt='receipt'
                  width={300}
                  height='auto'
                  onError={() => setImageError(true)}
                  style={imageError ? { display: 'none' } : undefined}
                />
                {imageError && (
                  <div
                    style={{
                      width: '300px',
                      minHeight: '180px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.subtext,
                      backgroundColor: theme.surfaceMuted
                    }}
                  >
                    Image unavailable
                  </div>
                )}
              </Col>
              <Col>
                <Row className='my-2'>
                  <Form.Group>
                    <Form.Label>Category</Form.Label>
                    <Form.Control
                      as='select'
                      value={rcategory}
                      onChange={(e) => setRcategory(e.target.value)}
                    >
                      <option value='New'>New</option>
                      <option value='Personal'>Personal</option>
                      <option value='Business'>Business</option>
                    </Form.Control>
                  </Form.Group>
                </Row>

                <Row className='my-2'>
                  <Form.Group>
                    <Form.Label>Meta Data</Form.Label>
                    <Form.Control
                      type='text'
                      as='textarea'
                      rows={3}
                      placeholder='Meta Data'
                      value={rmeta}
                      onChange={(e) => setRmeta(e.target.value)}
                    />
                  </Form.Group>
                </Row>

                <Row className='my-2'>
                  <Form.Group>
                    <Form.Label>Comments</Form.Label>
                    <Form.Control
                      as='textarea'
                      rows={3}
                      type='text'
                      placeholder='Comments'
                      value={rcptext}
                      onChange={(e) => setRcptext(e.target.value)}
                    ></Form.Control>
                  </Form.Group>
                </Row>
                <Row className='my-2'>
                  <Button
                    type='submit'
                    variant='outline-primary'
                    style={{
                      borderColor: theme.accent,
                      color: theme.accent,
                      backgroundColor: 'transparent'
                    }}
                  >
                    Update
                  </Button>
                </Row>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  )
}

export default ReceiptUpdateScreen
